import _ from 'lodash'
import {conversionConfig} from './conversionConfig'
import {isMobileOnly} from './mobileOnlyComponents/mobileOnlyComponentsUtils'
import type {IdToComponentMap, ObjMap, DeepStructure, Layout} from '../types'
import {getRecommendedHeight} from './structureConverter/structureConverterUtils'

export const isMergeVirtualGroup = (comp: DeepStructure): boolean => _.get(comp, 'componentType') === conversionConfig.VIRTUAL_GROUP_TYPES.MERGE
export const isRescaleVirtualGroup = (comp: DeepStructure): boolean => _.get(comp, 'componentType') === conversionConfig.VIRTUAL_GROUP_TYPES.RESCALE
export const isVirtualGroup = (comp: DeepStructure): boolean => isRescaleVirtualGroup(comp) || isMergeVirtualGroup(comp)

export function isDesktopOnlyComponent(component: DeepStructure): boolean {
    return _.get(component, ['conversionData', 'desktopOnly'], false)
}

export function isTextComponent(component: DeepStructure): boolean {
    return _.get(component, ['conversionData', 'category']) === 'text'
}

export function isVerticalText(component: DeepStructure) {
    return _.get(component, ['conversionData', 'isVerticalText'], false)
}

export function isGraphicComponent(component: DeepStructure): boolean {
    return _.get(component, ['conversionData', 'category']) === 'graphic' || isImageComponent(component)
}

export function isGroupComponent(component: DeepStructure): boolean {
    return component && component.componentType === 'wysiwyg.viewer.components.Group'
}

export function isAppWidgetComponent(component: DeepStructure): boolean {
    return component?.componentType === 'platform.components.AppWidget'
}

export function isColumnsContainerComponent(component: DeepStructure): boolean {
    return _.get(component, ['conversionData', 'category']) === 'columns'
}

export const isColumnComponent = (component: DeepStructure) => {
    return _.get(component, ['conversionData', 'category']) === 'column'
}

export const isBoxContainerComponent = (component: DeepStructure) => component.conversionData?.category === 'boxContainer'

export const isClassicSectionComponentByType = componentType => {
    return componentType === 'wysiwyg.viewer.components.ClassicSection'
}

export const isClassicSectionComponent = (component: DeepStructure) =>
    component.conversionData?.category === 'section' || isClassicSectionComponentByType(component.componentType)

export const isAnchorComponent = (component: DeepStructure) => {
    return component.componentType === 'wysiwyg.common.components.anchor.viewer.Anchor'
}

export function isImageComponent(component: DeepStructure): boolean {
    return _.get(component, ['conversionData', 'category']) === 'photo'
}

export function isMasterPage(component: DeepStructure) {
    return _.get(component, 'id') === 'masterPage'
}

export function isMasterPageChild(component: DeepStructure): boolean {
    return _.includes(['SITE_FOOTER', 'SITE_HEADER', 'SITE_PAGES', 'PAGES_CONTAINER'], component.id)
}

export function isDockingAllowed(comp: DeepStructure): boolean {
    return _.get(comp, ['conversionData', 'isDockingAllowed'])
}

export function isFixedPositionAllowed(comp: DeepStructure): boolean {
    return _.get(comp, ['conversionData', 'isFixedPositionAllowed'])
}

export function isFixedPositionElement(comp: DeepStructure): boolean {
    return _.get(comp, ['layout', 'fixedPosition'], false)
}

export function shouldConvertFixedPositionToAbsolute(comp: DeepStructure) {
    return _.get(comp, ['conversionData', 'convertFixedPositionToAbsolute'], false)
}

export function isExistsBeforeMerge(mobileComp: DeepStructure): boolean {
    return mobileComp.conversionData ? mobileComp.conversionData.existsBeforeMerge : false
}

export function shouldStretchToScreenWidth(component: DeepStructure): boolean {
    const shouldStretchHorizontally = comp =>
        _.get(comp.conversionData, 'isScreenWidth', false) ||
        _.get(comp.conversionData, 'stretchHorizontally', false) ||
        _.some(comp.components, shouldStretchHorizontally)

    return isSiteSegmentOrPage(component) || shouldStretchHorizontally(component)
}

export function extractComponentsFromStructureByType(root: DeepStructure, compTypes: string[]) {
    const children = getChildren(root)
    const extractedChildrenByType = _.remove(children, child => {
        const widgetRootChild = isAppWidgetComponent(child) && getRootChild(child)
        return _.includes(compTypes, child.componentType) || (widgetRootChild && _.includes(compTypes, widgetRootChild.componentType))
    })
    return _.concat(
        extractedChildrenByType,
        _.flatMap(children, child => extractComponentsFromStructureByType(child, compTypes))
    )
}

export function isScreenWidthComponent(component: DeepStructure): boolean {
    return _.get(component.conversionData, 'isScreenWidth', false) || _.some(component.components, isScreenWidthComponent)
}

export function translateComps(comps: DeepStructure[], x = 0, y = 0): void {
    _.forEach(comps, comp => {
        comp.layout.x += x
        comp.layout.y += y
    })
}

export function reparentComponent(parent: DeepStructure, newChild: DeepStructure, index?: number): void {
    addComponentsTo(parent, [newChild], index)
    translateComps([newChild], -parent.layout.x, -parent.layout.y)
}

function addComponentsInternal(container: DeepStructure, components: DeepStructure[], index?: number): void {
    const children = getChildren(container)

    if (!components.length || !children) {
        return
    }
    index = index !== undefined ? index : children.length
    // eslint-disable-next-line prefer-spread
    children.splice.apply(children, ([index, 0] as any[]).concat(components) as [number, number, ...any[]])
}

export function addComponentsTo(container: DeepStructure, components: DeepStructure[], index?: number): void {
    addComponentsInternal(container, components, index)
    _.forEach(components, comp => {
        if (container?.conversionData?.structureManager) {
            container.conversionData.structureManager.addToStructure(container, comp)
        }
    })
}

export function removeChildrenFrom(container: DeepStructure, componentsToRemove: DeepStructure[]): void {
    const children = getChildren(container)
    if (children && children.length > 0) {
        _.remove(children, child => _.includes(componentsToRemove, child))
    }
}

export function removeChildrenFromById(container: DeepStructure, componentsToRemoveIds: string[]): void {
    const children = getChildren(container)
    if (children && children.length > 0) {
        _.remove(children, child => _.includes(componentsToRemoveIds, child.id))
    }
}

export function removeGroup(group: DeepStructure, groupParent: DeepStructure) {
    if (!isGroupComponent(group)) {
        return
    }

    const groupIndex = _.findIndex(groupParent.components, {id: group.id})

    _.forEach(group.components.reverse(), curGroupedComponent => {
        addComponentsTo(groupParent, [curGroupedComponent], groupIndex)
        translateComps([curGroupedComponent], group.layout.x, group.layout.y)
    })
    _.remove(groupParent.components, group)
}

export function containsComponent(container: DeepStructure, componentMatcher: (comp: DeepStructure) => boolean): boolean {
    return _.some(getChildren(container), (child: DeepStructure) => componentMatcher(child) || (child && containsComponent(child, componentMatcher)))
}

export function getHeightAccordingToChildren(
    container: DeepStructure,
    children?: DeepStructure[],
    enforceShrinkEvenWithNoChildren?: boolean
): number | undefined {
    if (isMasterPage(container)) {
        return
    }

    if (!children || (!enforceShrinkEvenWithNoChildren && _.isEmpty(children))) {
        return
    }

    const lowestChildBottom = _(children)
        .reject(['conversionData.isInvisible', true])
        .reduce((lowest, child) => {
            return _.max([lowest, child.layout.y + child.layout.height])
        }, 0)

    if (hasTightYMargin(container) || hasTightBottomMargin(container)) {
        return lowestChildBottom
    }

    const bottomMargin = shouldStretchToScreenWidth(container)
        ? conversionConfig.ROOT_COMPONENT_MARGIN_Y
        : conversionConfig.COMPONENT_MOBILE_MARGIN_Y + _.get(container.conversionData, 'borderWidth', 0)

    return lowestChildBottom + bottomMargin
}

export function ensureContainerTightlyWrapsChildren(
    container: DeepStructure,
    children?: DeepStructure[],
    enforceShrinkEvenWithNoChildren?: boolean,
    defaultMinHeight = 0
): number {
    const heightByChildren = getHeightAccordingToChildren(container, children, enforceShrinkEvenWithNoChildren)

    if (_.isNumber(heightByChildren)) {
        const originalHeight = container.layout.height
        const recommendedHeight = getRecommendedHeight(container) || 0
        const minHeight = _.get(container, ['conversionData', 'minHeight'], defaultMinHeight)
        container.layout.height = Math.max(minHeight, heightByChildren, recommendedHeight)

        return container.layout.height - originalHeight
    }

    return 0
}

export function isSiteSegmentOrPage(component: DeepStructure): boolean {
    return isPageComponent(component) || isSiteSegment(component)
}

export function isPageComponent(component: DeepStructure): boolean {
    return component.type === 'Page'
}

export function isMobileOnlyElement(mobileComponent: DeepStructure): boolean {
    return _.get(mobileComponent, ['conversionData', 'mobileHints', 'mobileOnlyElement'])
}

export function isModifiedComponent(component: DeepStructure): boolean {
    return _.get(component, ['conversionData', 'mobileHints', 'modifiedByUser'], false)
}

export const isInModifiedComponent = (mobileComponent: DeepStructure, mobileComponents: Record<string, DeepStructure>) => {
    if (!mobileComponent || isPageComponent(mobileComponent)) {
        return false
    }

    const isModified = isModifiedComponent(mobileComponent)

    if (!isModified) {
        const parent = mobileComponents[getParentIdFromMobileHints(mobileComponent)]

        return isInModifiedComponent(parent, mobileComponents)
    }

    return isModified
}

export const isAuthorStudio = (component: DeepStructure): boolean => component.conversionData?.mobileHints?.author === 'studio'

export const isNewlyComponent = (component: DeepStructure) =>
    _.get(component, [
        'conversionData',
        'existsOnMobileBeforeMerge',
        //@ts-expect-error
        false
    ])

export const isDirectParentOfMOC = (component: DeepStructure) => _.get(component, ['conversionData', 'isDirectParentOfMobileOnly'], false)

export const componentListToMap = (compList): IdToComponentMap =>
    compList.reduce((map, comp) => map.set(comp.id, comp) && map, new Map<string, DeepStructure>())

export const isReparentedComponent = (comp: DeepStructure) => {
    return _.get(comp, ['conversionData', 'mobileHints', 'reparented'])
}

export const shouldKeepOnMobile = (component: DeepStructure) => {
    return isModifiedComponent(component) || isMobileOnly(component) || isDirectParentOfMOC(component)
}

export function isContainerComponent(component: DeepStructure): boolean {
    return component.type === 'Container'
}

export function isSiteSegment(component: DeepStructure): boolean {
    return _.has(component.conversionData, 'siteSegmentRole')
}

export function isAllowedToBeInMasterPage(component: DeepStructure): boolean {
    return isSiteSegment(component) || _.has(component.conversionData, 'isAllowedToBeChildOfMasterPage')
}

export function getComponentByIdFromStructure(componentId: string, component: DeepStructure): DeepStructure | null {
    if (component.id === componentId) {
        return <DeepStructure>component
    }
    let res = null
    _.find(getChildren(component), comp => {
        res = getComponentByIdFromStructure(componentId, comp)
        return res
    })
    return res
}

export function unifyGroups(groups: string[][], groupOverflowThreshold?: number): void {
    if (groups.length > groupOverflowThreshold) {
        groups.length = 0
        return
    }
    const haveCommonElements = (arr1, arr2) => _.size(_.intersection(arr1, arr2)) > 0
    for (let i = groups.length - 1; i >= 0; i--) {
        const j = _.findLastIndex(groups, (group, index) => index < i && haveCommonElements(groups[i], group))
        if (j !== -1) {
            groups[j] = groups[j].concat(_.difference(groups[i], groups[j]))
            groups.splice(i, 1)
        }
    }
}

export function getComponentsByIds(container: DeepStructure, compIds: string[]): DeepStructure[] {
    const children = getChildren(container)
    return _.map(compIds, id => _.find(children, {id}) || null)
}

export function getParent(componentId: string, root: DeepStructure): DeepStructure | null {
    const children = getChildren(root)
    if (_.find(children, {id: componentId})) {
        return root
    }
    let parent = null
    _.find(children, child => {
        parent = getParent(componentId, child)
        return parent
    })
    return parent
}

export const getPresetFromConversionData = comp => {
    return _.get(comp, ['conversionData', 'preset'], undefined)
}

export const getParentIdFromMobileHints = (component: DeepStructure) => component.conversionData?.mobileHints?.parent

export const setParentIdToMobileHints = (component, parentId) => _.set(component, ['conversionData', 'mobileHints', 'parent'], parentId)

export function getSnugLayout(components: DeepStructure[]): Layout {
    if (!components || components.length === 0) {
        return undefined
    }
    const mostLeft = <number>_.min(_.map(components, 'layout.x'))
    const mostTop = <number>_.min(_.map(components, 'layout.y'))
    const mostRight = _.max(_.map(components, c => c.layout.x + c.layout.width))
    const mostBottom = _.max(_.map(components, c => c.layout.y + c.layout.height))
    return {
        x: mostLeft,
        y: mostTop,
        width: mostRight - mostLeft,
        height: mostBottom - mostTop,
        rotationInDegrees: 0
    }
}
/**
 * TODO: INVESTIGATE IS TINY MENU TOTALY DEPRECATED AND REMOVE EVERITHYNG RELATED TO TINY MENU
 * https://jira.wixpress.com/browse/WEED-25100
 */
export function getTinyMenuDefaultPosition(): {
    x: number
    y: number
    rotationInDegrees: number
} {
    return {
        x: conversionConfig.MOBILE_WIDTH - (conversionConfig.TINY_MENU_SIZE + conversionConfig.SITE_SEGMENT_PADDING_X),
        y: conversionConfig.ROOT_COMPONENT_MARGIN_Y,
        rotationInDegrees: 0
    }
}

export const getChildren = (comp: DeepStructure): DeepStructure[] | undefined => {
    // Continue returning comp.children until we are sure no code path
    // pass in a structure with children property
    return comp.components ?? comp.children
}

export const getRootChild = (comp: DeepStructure): DeepStructure => _.get(comp, ['components', 0]) || _.get(comp, ['children', 0])

export function getRangesOverlap(range1: number[] = [], range2: number[] = []) {
    const getSortedRangesOverlap = (r1, r2) => Math.min(r1[1], r2[1]) - r2[0]
    return range1[0] <= range2[0] ? getSortedRangesOverlap(range1, range2) : getSortedRangesOverlap(range2, range1)
}

export function getYOverlap(comp1: DeepStructure, comp2: DeepStructure): number {
    const getYProjection = comp => [comp.layout.y, comp.layout.height + comp.layout.y]
    return getRangesOverlap(getYProjection(comp1), getYProjection(comp2))
}

export function getXOverlap(comp1: DeepStructure, comp2: DeepStructure): number {
    if (isScreenWidthComponent(comp1)) {
        return comp2.layout.width
    }
    if (isScreenWidthComponent(comp2)) {
        return comp1.layout.width
    }
    const getXProjection = comp => [comp.layout.x, comp.layout.width + comp.layout.x]
    return getRangesOverlap(getXProjection(comp1), getXProjection(comp2))
}

const getArea = (component: DeepStructure) => component.layout.width * component.layout.height

export function hasGreaterArea(comp1, comp2) {
    if (isScreenWidthComponent(comp1)) {
        return comp1.layout.height >= comp2.layout.height || getArea(comp1) > getArea(comp2)
    }
    if (isScreenWidthComponent(comp2)) {
        return false
    }
    return getArea(comp1) > getArea(comp2)
}

export function haveSufficientOverlap(comp1: DeepStructure, comp2: DeepStructure, overlapToMinAreaRationThreshold?): boolean {
    const xOverlap = getXOverlap(comp1, comp2)
    const yOverlap = getYOverlap(comp1, comp2)
    if (xOverlap <= 0 || yOverlap <= 0) {
        return false
    }
    const overlapArea = xOverlap * yOverlap
    const minCompArea = Math.min(getArea(comp1), getArea(comp2))
    return overlapArea > 0 && overlapArea / minCompArea >= overlapToMinAreaRationThreshold
}

export function isEmptyContainer(component: DeepStructure): boolean {
    if (!isContainerComponent(component)) {
        return false
    }
    if (component.conversionData.category === 'columns') {
        return _.size(component.components) === 1 && _.isEmpty(component.components[0].components)
    }
    return _.isEmpty(component.components)
}

export function shouldReparentCompToChildOfContainer(comp, container): boolean {
    return isColumnsContainerComponent(container) && _.get(comp, ['conversionData', 'isInvisible'], false)
}

export const getFlattenListOfComponents = (compStructure: DeepStructure, isMobile = false): DeepStructure[] => {
    const queue = [[compStructure]]
    for (const innerQueue of queue) {
        _.forEach(innerQueue, child => {
            const children = isMobile ? _.get(child, 'mobileComponents') : getChildren(child)
            if (!_.isEmpty(children)) {
                queue.push(<DeepStructure[]>children)
            }
        })
    }
    return _.flatten(queue)
}

export function getAllCompsInStructure(compStructure: DeepStructure, isMobile = false, filterFunc?: (comp: DeepStructure) => boolean): ObjMap<DeepStructure> {
    const listOfComponents = getFlattenListOfComponents(compStructure, isMobile)
    const filteredListOfComponents: ObjMap<DeepStructure> = _(listOfComponents).remove(filterFunc).keyBy('id').value()

    return filteredListOfComponents
}

export const isSemiTransparentContainer = comp => _.get(comp, ['conversionData', 'isSemiTransparentContainer'], false)

export const hasTightTopMargin = comp => comp.conversionData.hasTightTopMargin
export const hasTightBottomMargin = comp => comp.conversionData.hasTightBottomMargin
export const hasTightYMargin = comp => comp.conversionData.hasTightYMargin
